/**
 * Quick Actions Service
 * Bridges speed dial FAB actions to command palette
 * Provides unified access to quick actions based on user role
 */

import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DialogType } from '../../shared/dialog/dialog.service';
import { DialogConfig } from '../../shared/dialog/dialog-config';
import { AccountMembership, AuthorizationLevel } from 'app/API.service.custom';
import { canEditInventory } from 'app/core/access/route-access-policy';
import { isSampleDataModeEnabled } from 'app/core/amplify-shim';
import {
  ASSISTANT_ACTION_DEFINITIONS,
  AssistantActionDefinition
} from './assistant-actions.registry';

export interface QuickAction {
  id: string;
  text: string;
  tooltip: string;
  icon: string;
  category: 'admin' | 'user' | 'common';
  keywords?: string[];
  component?: Type<any>;  // Component to open in dialog
  route?: string;   // Route to navigate to
  rootRoute?: string;
  subRoutes?: string[];
  dialogType?: DialogType;
  closeOnClickOutside?: boolean;
  dialogConfig?: DialogConfig;
}

@Injectable({ providedIn: 'root' })
export class QuickActionsService {

  private actionsSubject = new BehaviorSubject<QuickAction[]>([]);
  public actions$ = this.actionsSubject.asObservable();

  private currentRole: AuthorizationLevel = AuthorizationLevel.MEMBER;
  private currentAccount: AccountMembership | null = null;
  private readonly defaultNonAdminIds: string[] = [
    'add-reminder',
    'get_assistance',
    'add_single_lead',
    'add_company',
    'log_call',
    'log_meeting',
    'add_note',
    'add_document'
  ];

  private readonly defaultAdminIds: string[] = [
    'add-reminder',
    'get_support_admin',
    'add_single_lead',
    'upload_lead',
    'define_distribution',
    'invite_user',
    'manage_users_teams',
    'create_template',
    'create_form',
    'add_document'
  ];

  constructor() {
    // Initialize with default actions
    this.loadActions();
  }

  /**
   * Set user role and reload actions
   */
  setUserRole(role: AuthorizationLevel): void {
    this.currentRole = role;
    this.currentAccount = null;
    this.loadActions();
  }

  /** Use the full membership when account-level permissions affect an action. */
  setActiveAccount(account: AccountMembership | null): void {
    this.currentAccount = account;
    this.currentRole = account?.authorizationLevel || AuthorizationLevel.MEMBER;
    this.loadActions();
  }

  /**
   * Get all available quick actions for current user
   */
  getActions(): QuickAction[] {
    return this.actionsSubject.value;
  }

  /**
   * Get prioritized default actions (aligned with historical speed dial)
   */
  getDefaultActions(): QuickAction[] {
    const prioritizedIds = this.isAdminRole(this.currentRole)
      ? this.defaultAdminIds
      : this.defaultNonAdminIds;

    const available = this.actionsSubject.value;
    const selected: QuickAction[] = [];
    const seen = new Set<string>();

    for (const id of prioritizedIds) {
      const action = available.find(a => a.id === id);
      if (action && !seen.has(action.id)) {
        selected.push(action);
        seen.add(action.id);
      }
    }

    // Fill the remaining slots with other allowed actions to avoid empty lists
    for (const action of available) {
      if (!seen.has(action.id)) {
        selected.push(action);
        seen.add(action.id);
      }
    }

    return selected;
  }

  /**
   * Get actions observable
   */
  getActions$(): Observable<QuickAction[]> {
    return this.actions$;
  }

  /**
   * Search actions by text
   */
  searchActions(query: string): QuickAction[] {
    const lowerQuery = query.toLowerCase();
    return this.actionsSubject.value.filter(action =>
      action.text.toLowerCase().includes(lowerQuery) ||
      action.tooltip.toLowerCase().includes(lowerQuery) ||
      (action.keywords || []).some(keyword =>
        lowerQuery.includes(keyword.toLowerCase()) ||
        keyword.toLowerCase().includes(lowerQuery)
      )
    );
  }

  /**
   * Get action by ID
   */
  getAction(id: string): QuickAction | undefined {
    return this.actionsSubject.value.find(a => a.id === id);
  }

  /**
   * Load actions based on user role
   */
  private loadActions(): void {
    const available = ASSISTANT_ACTION_DEFINITIONS
      .filter(action => this.isAllowedForRole(action));

    const mapped: QuickAction[] = available.map(action => ({
      id: action.id,
      text: action.text,
      tooltip: action.tooltip,
      icon: action.icon,
      category: action.category,
      keywords: action.keywords,
      component: action.component,
      route: action.route,
      rootRoute: action.rootRoute,
      subRoutes: action.subRoutes,
      dialogType: action.dialogType,
      closeOnClickOutside: action.closeOnClickOutside,
      dialogConfig: action.dialogConfig
    }));

    this.actionsSubject.next(mapped);
  }

  private isAdminRole(role: AuthorizationLevel): boolean {
    return role === AuthorizationLevel.ADMIN || role === AuthorizationLevel.SUPERADMIN;
  }

  private isAllowedForRole(action: AssistantActionDefinition): boolean {
    if (!action.allowedRoles || action.allowedRoles.length === 0) {
      return this.hasRequiredCapability(action);
    }
    return action.allowedRoles.includes(this.currentRole) && this.hasRequiredCapability(action);
  }

  private hasRequiredCapability(action: AssistantActionDefinition): boolean {
    if (!action.requiredCapability) return true;
    if (action.requiredCapability === 'catalogEdit') {
      return !!this.currentAccount && canEditInventory(this.currentAccount) && !isSampleDataModeEnabled();
    }
    return false;
  }

  /**
   * Map material icon to emoji for command palette
   */
  getEmojiForIcon(icon: string): string {
    const iconMap: Record<string, string> = {
      'circle_notifications': '🔔',
      'help': '❓',
      'person': '👤',
      'upload_file': '📤',
      'alt_route': '🔀',
      'group_add': '👥',
      'people': '👥',
      'message': '💬',
      'list_alt': '📋',
      'cloud_upload': '☁️',
      'business': '🏢',
      'phone': '📞',
      'edit': '📝'
    };

    return iconMap[icon] || '⚡';
  }
}
